import { Navigate, useNavigate } from 'react-router-dom'
import { Box } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useIsMutating } from '@tanstack/react-query'

import { LoginRoutePathsEnum } from '@/modules/login/routes/loginRouteConfig.types'
import { passwordResetVerifyFormSchema } from '@/modules/passwordReset/components/PasswordResetVerifyForm/PasswordResetVerifyForm.schema'
import type { PasswordResetVerifyFormSchema } from '@/modules/passwordReset/components/PasswordResetVerifyForm/PasswordResetVerifyForm.types'
import { passwordResetVerifyFormDefaultValues } from '@/modules/passwordReset/components/PasswordResetVerifyForm/PasswordResetVerifyForm.values'

import { useUserStore } from '@/store/user.store'

import { Button } from '@/components/Button/Button'
import { PasswordInput } from '@/components/PasswordInput/PasswordInput'
import { TextInput } from '@/components/TextInput/TextInput'

import { useRolanWealthServiceVerifyControllerVerifyResetPasswordToken } from '@/__generated__/queries'

import { getUserDefaultPath } from '@/helpers/getUserDefaultPath.helper'

export function PasswordResetVerifyForm() {
  const user = useUserStore.use.user()
  const currentRole = useUserStore.use.currentRole()
  const navigate = useNavigate()

  const queryParameters = new URLSearchParams(window.location.search)
  const token = queryParameters.get('token') || ''

  const isMutating = useIsMutating({
    exact: true,
  })

  const form = useForm<PasswordResetVerifyFormSchema>({
    initialValues: { ...passwordResetVerifyFormDefaultValues, token },
    validate: zodResolver(passwordResetVerifyFormSchema),
  })

  const passwordResetVerifyMutation =
    useRolanWealthServiceVerifyControllerVerifyResetPasswordToken({
      onSuccess: data => {
        notifications.show({
          color: 'green',
          message: data.message,
          title: 'Password changed',
        })

        navigate(LoginRoutePathsEnum.LOGIN)
      },
    })

  const handleSubmit = form.onSubmit(values => {
    passwordResetVerifyMutation.mutate({
      requestBody: values,
    })
  })

  if (user) {
    return <Navigate to={getUserDefaultPath(user, currentRole)} />
  }

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <Box mt={48}>
        <TextInput
          label="Email"
          placeholder="Enter your email address"
          {...form.getInputProps('email')}
        />

        <PasswordInput
          label="Password"
          placeholder="Enter your password"
          {...form.getInputProps('password')}
        />

        <PasswordInput
          label="Confirm Password"
          placeholder="Confirm your password"
          {...form.getInputProps('confirmPassword')}
        />
      </Box>

      <Box mt={56}>
        <Button loading={!!isMutating} type="submit">
          Reset Password
        </Button>
      </Box>
    </Box>
  )
}
